import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';

import {
  AccessSelect,
  AccessTextField,
} from '@changegps/horizon-component-library';
import { Stack } from '@mui/material';

import { updateBillingDetails } from '../../api';
import { AU_STATE_OPTIONS } from '../../constants';
import { BillingDetails } from '../../types';

interface BillingDetailsFormProps {
  billingDetails?: BillingDetails | null;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  companyID?: string;
}

export interface BillingDetailsFormRef {
  submitForm: () => void;
}

export const BillingDetailsForm = forwardRef<
  BillingDetailsFormRef,
  BillingDetailsFormProps
>(({ billingDetails, onSuccess, onError, companyID }, ref) => {
  const [formData, setFormData] = useState({
    abn: billingDetails?.abn || '',
    email: billingDetails?.email || '',
    address: {
      line1: billingDetails?.address?.line1 || '',
      line2: billingDetails?.address?.line2 || '',
      city: billingDetails?.address?.city || '',
      state: billingDetails?.address?.state || '',
      postal_code: billingDetails?.address?.postal_code || '',
    },
    phone: billingDetails?.phone || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = useCallback(
    (field: string, value: string) => {
      if (field.startsWith('address.')) {
        const addressField = field.split('.')[1];
        setFormData((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            [addressField]: value,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [field]: value,
        }));
      }

      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: '',
        }));
      }
    },
    [errors],
  );

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!formData.abn.trim()) {
      newErrors.abn = 'ABN is required';
    } else if (!/^\d{11}$/.test(formData.abn.replace(/\s/g, ''))) {
      newErrors.abn = 'ABN must be 11 digits';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.address.line1.trim()) {
      newErrors['address.line1'] = 'Address Line 1 is required';
    }

    if (!formData.address.city.trim()) {
      newErrors['address.city'] = 'City is required';
    }

    if (!formData.address.state.trim()) {
      newErrors['address.state'] = 'State is required';
    }

    if (!formData.address.postal_code.trim()) {
      newErrors['address.postal_code'] = 'Postal code is required';
    } else if (!/^\d{4}$/.test(formData.address.postal_code)) {
      newErrors['address.postal_code'] = 'Postal code must be 4 digits';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (
      !/^(\+61|0)[2-9]\d{8}$/.test(formData.phone.replace(/\s/g, ''))
    ) {
      newErrors.phone = 'Please enter a valid Australian phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const submitForm = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await updateBillingDetails(companyID ?? '', formData);
      onSuccess?.('Billing details updated successfully');
    } catch (error) {
      onError?.('Failed to update billing details. Please try again.');
    }
  }, [formData, validateForm, companyID, onSuccess, onError]);

  useImperativeHandle(
    ref,
    () => ({
      submitForm,
    }),
    [submitForm],
  );

  return (
    <Stack spacing={1.5}>
      <AccessTextField
        label="ABN"
        value={formData.abn}
        onChange={(e) => handleInputChange('abn', e.target.value)}
        error={!!errors.abn}
        helperText={errors.abn}
        placeholder="Enter 11-digit ABN"
        required
      />

      <AccessTextField
        label="Email"
        value={formData.email}
        onChange={(e) => handleInputChange('email', e.target.value)}
        error={!!errors.email}
        helperText={errors.email}
        placeholder="Enter email address"
        type="email"
        required
      />

      <Stack direction="row" spacing={2}>
        <AccessTextField
          label="Address Line 1"
          value={formData.address.line1}
          onChange={(e) => handleInputChange('address.line1', e.target.value)}
          error={!!errors['address.line1']}
          helperText={errors['address.line1']}
          placeholder="Street address"
          fullWidth
          required
        />
        <AccessTextField
          label="Address Line 2"
          value={formData.address.line2}
          onChange={(e) => handleInputChange('address.line2', e.target.value)}
          placeholder="Apartment, suite, etc. (optional)"
          fullWidth
        />
      </Stack>

      <Stack direction="row" spacing={2}>
        <AccessTextField
          label="City"
          value={formData.address.city}
          onChange={(e) => handleInputChange('address.city', e.target.value)}
          error={!!errors['address.city']}
          helperText={errors['address.city']}
          placeholder="City"
          fullWidth
          required
        />
        <AccessSelect
          label="State"
          value={formData.address.state}
          onChange={(e) =>
            handleInputChange('address.state', e.target.value as string)
          }
          error={!!errors['address.state']}
          helperText={errors['address.state']}
          placeholder="State/Territory"
          fullWidth
          options={AU_STATE_OPTIONS}
          optionKeys={{ label: 'name', value: 'code' }}
          required
        />
      </Stack>

      <Stack direction="row" spacing={2}>
        <AccessTextField
          label="Postal Code"
          value={formData.address.postal_code}
          onChange={(e) =>
            handleInputChange('address.postal_code', e.target.value)
          }
          error={!!errors['address.postal_code']}
          helperText={errors['address.postal_code']}
          placeholder="4-digit postcode"
          fullWidth
          required
        />
        <AccessTextField
          label="Phone"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          error={!!errors.phone}
          helperText={errors.phone}
          placeholder="Mobile or landline"
          fullWidth
          required
        />
      </Stack>
    </Stack>
  );
});

BillingDetailsForm.displayName = 'BillingDetailsForm';
